/**
 * The register's search and filter panel.
 *
 * Split out of `AriregisterSection.tsx`. It holds no state of its own: the
 * committed filter object and the typing draft both live in the list, because
 * the debounce that turns one into the other has to survive this component
 * re-rendering, and the chips below have to be able to clear both halves at once
 * (see `removeChip` in `AriregisterList`).
 *
 * The "More filters" grid lives in `AdvancedFilters.tsx`.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowDownUp, Map as MapIcon, SlidersHorizontal, Users, X } from "lucide-react";
import { Combobox, type ComboboxOption, type DateRangeValue } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { COUNTIES, type ArOrder, type ArSort } from "../../../lib/ariregister";
import {
  ANY, type AudienceDraft, type AudienceFilter,
} from "../../../lib/audienceFilter";
import type { AudienceChip } from "../../../lib/audienceChips";
import { Btn, MenuField, SearchField, Toolbar, ToolbarGap } from "../../ui";
import { AdvancedFilters } from "./AdvancedFilters";
import { FilterField } from "./filterFields";
import { parseSortValue, sortOptions, sortValue } from "./sortOptions";

export function AriregisterFilters({
  filters,
  draft,
  setDraft,
  patch,
  showAdvanced,
  onToggleAdvanced,
  advancedCount,
  activityOptions,
  regRange,
  onRegRange,
  chips,
  actions,
  onRemoveChip,
  onClear,
  hasFilters,
  sort,
  order,
  onSort,
  columnPicker,
}: {
  filters: AudienceFilter;
  draft: AudienceDraft;
  setDraft: (fn: (d: AudienceDraft) => AudienceDraft) => void;
  patch: (p: Partial<AudienceFilter>) => void;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  advancedCount: number;
  activityOptions: ComboboxOption[];
  regRange: DateRangeValue | null;
  onRegRange: (r: DateRangeValue | null) => void;
  chips: AudienceChip[];
  /** Rendered at the end of the chip row, which only exists while something is
   *  filtered - so an action placed here is one that only makes sense against a
   *  selection rather than against the whole register. */
  actions?: ReactNode;
  onRemoveChip: (c: AudienceChip) => void;
  onClear: () => void;
  hasFilters: boolean;
  sort: ArSort;
  order: ArOrder;
  onSort: (sort: ArSort, order: ArOrder) => void;
  /** The table's column switch. Handed in rather than built here: which columns
   *  exist is the table's business, but the control belongs in this toolbar
   *  next to the other things that change what the list looks like. */
  columnPicker?: ReactNode;
}) {
  const t = useT();

  return (
    <>
      <Toolbar>
        <SearchField
          value={draft.q}
          onChange={(v) => setDraft((d) => ({ ...d, q: v }))}
          placeholder={t("ariregister.searchPlaceholder")}
          className="min-w-[16rem] flex-1"
        />
        {/* The register's other two views. They used to be underlined text
            hanging off the end of the page's intro sentence, one of them
            labelled with a whole parenthetical clause - so the two things you
            can DO with this register read as footnotes to a paragraph, sat
            nowhere near the controls, and were the smallest click targets on the
            screen. They are controls, so they live in the control row. */}
        <span className="crm-navseg">
          <Link to="isikud" className="crm-navseg-b">
            <Users className="size-4" />
            {t("ariregister.toPersonSearch")}
          </Link>
          <Link to="kaart" className="crm-navseg-b">
            <MapIcon className="size-4" />
            {t("ariregister.toMap")}
          </Link>
        </span>
        {/* Every row carries the same icon, so the trigger - which shows only
            the chosen row - reads as an ORDER and not as a filter. It used to be
            labelled "Name", which is what the first option happens to say, and
            an operator looking for "newest first" had no reason to open it. */}
        <MenuField
          value={sortValue(sort, order)}
          onChange={(v) => {
            const picked = parseSortValue(v);
            if (picked) onSort(picked.sort, picked.order);
          }}
          label={t("ariregister.sort.label")}
          options={sortOptions(sort, order, t).map((o) => ({
            ...o,
            icon: <ArrowDownUp className="size-4 shrink-0 opacity-70" />,
          }))}
        />
        <ToolbarGap />
        <Btn tone={showAdvanced ? "dark" : "plain"} onClick={onToggleAdvanced}>
          <SlidersHorizontal className="size-4" />
          {t("ariregister.filter.more")}
          {advancedCount > 0 ? <span className="crm-tnum opacity-70">{advancedCount}</span> : null}
        </Btn>
      </Toolbar>

      <Toolbar>
        <FilterField
          value={filters.status}
          onChange={(v) => patch({ status: v })}
          label={t("ariregister.filter.status")}
          anyLabel={t("ariregister.filter.statusAny")}
          options={[
            { v: "R", label: t("ariregister.status.R") },
            { v: "L", label: t("ariregister.status.L") },
            { v: "N", label: t("ariregister.status.N") },
          ]}
        />
        <FilterField
          value={filters.county}
          onChange={(v) => patch({ county: v })}
          label={t("ariregister.filter.county")}
          anyLabel={t("ariregister.filter.countyAny")}
          options={COUNTIES.map((c) => ({ v: c, label: c }))}
        />
        {/* The activity picker stays the shared `Combobox`: it is a searchable
            three-level tree of several hundred codes, which is not what the
            CRM's flat menu is for. `crm-fld-combo` gives it the toolbar's
            raised pill, so it stops being the one bordered box in the row. */}
        <span className="min-w-[18rem] flex-1">
          <Combobox
            className="crm-fld-combo"
            options={activityOptions}
            value={filters.emtak}
            onChange={(v) => patch({ emtak: v })}
            clearable
            placeholder={t("ariregister.filter.activity")}
            searchPlaceholder={t("ariregister.filter.activitySearch")}
            emptyLabel={t("ariregister.filter.activityEmpty")}
            popoverClassName="w-[24rem]"
          />
        </span>
        {/* Last on the second row rather than beside "More filters", where it
            was: the first row is already exactly full at 1280 px, so the picker
            wrapped onto a line of its own and read as a stray. Here the activity
            field gives up the width instead, and the row now says what you
            filter by and what you see - which is one thought, not two. */}
        {columnPicker}
      </Toolbar>

      {showAdvanced ? (
        <AdvancedFilters
          filters={filters}
          draft={draft}
          setDraft={setDraft}
          patch={patch}
          regRange={regRange}
          onRegRange={onRegRange}
        />
      ) : null}

      {chips.length > 0 || hasFilters ? (
        <div className="mb-3.5 flex flex-wrap items-center gap-1.5 px-1.5">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => onRemoveChip(c)}
              className="crm-chip hover:text-red-600"
              title={t("ariregister.filter.clear")}
            >
              {c.label}
              <X className="size-3" />
            </button>
          ))}
          {hasFilters ? (
            <Btn small onClick={onClear}>
              <X className="size-3.5" />
              {t("ariregister.filter.clear")}
            </Btn>
          ) : null}
          {actions}
        </div>
      ) : null}
    </>
  );
}
